import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import { ReservationsService } from '@/api/services/reservations.service';
import { useAuthStore } from '@/composition/use-auth';
import { PermissionType } from '@/enums/PermissionType';
import { getServerErrorStatus } from '@/domain/reservations/reservation-errors';
import type {
  IReservation,
  IReservationUpcoming,
  IReservationsDay,
} from '@/interfaces/IReservation';
import { useRestaurantStore } from './restaurant.store';

let daySeq = 0;
let upcomingSeq = 0;
type TLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export const useReservationsStore = defineStore('reservations', () => {
  const restaurant = useRestaurantStore();
  const auth = useAuthStore();
  const pendingCount = ref<number | null>(null);
  let pendingSeq = 0;
  const day = shallowRef<IReservationsDay | null>(null);
  const dayDate = ref<string | null>(null);
  const dayBranchId = ref<number | null>(null);
  const dayStatus = ref<TLoadStatus>('idle');
  const dayError = shallowRef<unknown>(null);
  const selectedReservationId = ref<number | null>(null);
  const upcoming = shallowRef<IReservationUpcoming[] | null>(null);
  const upcomingSettings = shallowRef<{
    enabled: boolean;
    toleranceMin: number;
    serverTime: string | null;
  } | null>(null);
  const upcomingStatus = ref<TLoadStatus>('idle');
  const upcomingError = shallowRef<unknown>(null);
  const upcomingConsumers = ref(0);
  const upcomingAccessDenied = ref(false);
  const revision = ref(0);

  function scope() {
    return {
      restaurantId: restaurant.restaurant?.id,
      branchId: restaurant.selectedBranch?.id,
    };
  }

  function isCurrent(restaurantId: number, branchId: number) {
    const current = scope();
    return (
      current.restaurantId === restaurantId && current.branchId === branchId
    );
  }

  async function loadDay(date: string, { silent = false } = {}) {
    const { restaurantId, branchId } = scope();
    if (!restaurantId || !branchId || restaurant.effectiveTier < 2) return null;
    const sequence = ++daySeq;
    const sameDay = dayBranchId.value === branchId && dayDate.value === date;
    if (!sameDay) day.value = null;
    dayDate.value = date;
    dayBranchId.value = branchId;
    dayError.value = null;
    if (!silent || !day.value) dayStatus.value = 'loading';
    try {
      const response = await ReservationsService.getDay(
        restaurantId,
        branchId,
        date
      );
      if (sequence !== daySeq || !isCurrent(restaurantId, branchId))
        return null;
      day.value = response;
      dayStatus.value = 'ready';
      return response;
    } catch (error) {
      if (sequence !== daySeq || !isCurrent(restaurantId, branchId))
        return null;
      dayError.value = error;
      dayStatus.value = 'error';
      throw error;
    }
  }

  async function loadUpcoming() {
    const { restaurantId, branchId } = scope();
    if (
      !restaurantId ||
      !branchId ||
      restaurant.effectiveTier < 2 ||
      upcomingAccessDenied.value ||
      upcomingConsumers.value < 1
    )
      return null;
    const sequence = ++upcomingSeq;
    upcomingError.value = null;
    if (!upcoming.value) upcomingStatus.value = 'loading';
    try {
      const response = await ReservationsService.getUpcoming(
        restaurantId,
        branchId
      );
      if (
        sequence !== upcomingSeq ||
        !isCurrent(restaurantId, branchId) ||
        !upcomingConsumers.value
      )
        return null;
      upcoming.value = response.enabled ? response.reservations : [];
      upcomingSettings.value = {
        enabled: response.enabled,
        toleranceMin: response.enabled ? response.toleranceMin : 0,
        serverTime: response.enabled ? response.serverTime : null,
      };
      upcomingStatus.value = 'ready';
      return response;
    } catch (error) {
      if (sequence !== upcomingSeq || !isCurrent(restaurantId, branchId))
        return null;
      upcomingError.value = error;
      upcomingStatus.value = 'error';
      if ([401, 403].includes(getServerErrorStatus(error) ?? 0)) {
        upcomingAccessDenied.value = true;
        upcoming.value = null;
      }
      throw error;
    }
  }

  async function loadPendingCount() {
    const sequence = ++pendingSeq;
    const { restaurantId, branchId } = scope();
    const canRead = () =>
      restaurant.effectiveTier >= 2 &&
      auth.permissionSet.has(PermissionType.ReservationsManage);
    if (!restaurantId || !branchId || !canRead()) {
      pendingCount.value = null;
      return;
    }
    try {
      const count = await ReservationsService.getPendingCount(
        restaurantId,
        branchId
      );
      if (sequence !== pendingSeq || !isCurrent(restaurantId, branchId)) return;
      pendingCount.value = canRead() ? count : null;
    } catch {
      if (sequence === pendingSeq && isCurrent(restaurantId, branchId))
        pendingCount.value = null;
    }
  }

  /** Called by socket and timer callbacks; each pane retains its own retry/error state. */
  async function refreshActive() {
    const jobs: Promise<unknown>[] = [loadPendingCount()];
    if (dayDate.value && dayBranchId.value === restaurant.selectedBranch?.id)
      jobs.push(loadDay(dayDate.value, { silent: true }));
    if (upcomingConsumers.value > 0) jobs.push(loadUpcoming());
    await Promise.allSettled(jobs);
  }

  function setDayRows(rows: IReservation[]) {
    if (!day.value) return;
    day.value = {
      ...day.value,
      reservations: rows,
      shifts: day.value.shifts.map((shift) => ({
        ...shift,
        bookedCovers: rows
          .filter(
            (row) =>
              !['finished', 'cancelled', 'no_show'].includes(row.status) &&
              Date.parse(row.startsAt) >= Date.parse(shift.startsAt) &&
              Date.parse(row.startsAt) < Date.parse(shift.endsAt)
          )
          .reduce((total, row) => total + row.partySize, 0),
      })),
    };
  }

  function patchReservation(updated: IReservation) {
    if (!isCurrent(updated.restaurantId, updated.branchId)) return;
    // Invalidate snapshots requested before this mutation committed.
    if (day.value && dayBranchId.value === updated.branchId) {
      ++daySeq;
      const rows = day.value.reservations.filter(
        (row) => row.id !== updated.id
      );
      if (updated.serviceDate === dayDate.value) rows.push(updated);
      rows.sort((a, b) => a.startsAt.localeCompare(b.startsAt) || a.id - b.id);
      setDayRows(rows);
      dayStatus.value = 'ready';
      dayError.value = null;
    }
    if (upcoming.value) {
      ++upcomingSeq;
      const rows = upcoming.value.filter((row) => row.id !== updated.id);
      const startsAt = Date.parse(updated.startsAt);
      const now = Date.now();
      const withinWindow =
        startsAt <= now + 90 * 60000 &&
        startsAt >=
          now - ((upcomingSettings.value?.toleranceMin ?? 15) + 360) * 60000;
      if (
        (updated.status === 'seated' && updated.sessionStatus === 1) ||
        (['confirmed', 'arrived'].includes(updated.status) && withinWindow)
      ) {
        rows.push({
          id: updated.id,
          guestName: updated.guestName,
          partySize: updated.partySize,
          startsAt: updated.startsAt,
          status: updated.status,
          tableIds: updated.tableIds,
          depositAmount: updated.depositAmount,
          depositStatus: updated.depositStatus,
          depositAppliedAt: updated.depositAppliedAt,
          depositAppliedAmount: updated.depositAppliedAmount,
          depositUnappliedAmount: updated.depositUnappliedAmount,
          notesGuest: updated.notesGuest,
          seatedAt: updated.seatedAt,
          tableSessionId: updated.tableSessionId,
        });
      }
      upcoming.value = rows.sort(
        (a, b) => a.startsAt.localeCompare(b.startsAt) || a.id - b.id
      );
    }
    revision.value += 1;
  }

  /** A POS-only mutation cannot manufacture a protected staff row. */
  function patchUpcomingReservation(
    updated: IReservationUpcoming,
    restaurantId: number,
    branchId: number
  ) {
    if (!isCurrent(restaurantId, branchId) || !upcoming.value) return;
    ++upcomingSeq;
    upcoming.value = upcoming.value.map((row) =>
      row.id === updated.id ? updated : row
    );
    revision.value += 1;
  }

  function removeFromDay(id: number) {
    if (!day.value) return;
    ++daySeq;
    setDayRows(day.value.reservations.filter((row) => row.id !== id));
    if (selectedReservationId.value === id) selectedReservationId.value = null;
  }

  function select(id: number | null) {
    selectedReservationId.value = id;
  }
  function acquireUpcoming() {
    upcomingConsumers.value += 1;
  }
  function releaseUpcoming() {
    upcomingConsumers.value = Math.max(0, upcomingConsumers.value - 1);
    if (!upcomingConsumers.value) ++upcomingSeq;
  }

  function clearForBranch() {
    ++pendingSeq;
    pendingCount.value = null;
    ++daySeq;
    ++upcomingSeq;
    day.value = null;
    dayDate.value = null;
    dayBranchId.value = null;
    dayStatus.value = 'idle';
    dayError.value = null;
    selectedReservationId.value = null;
    upcoming.value = null;
    upcomingSettings.value = null;
    upcomingStatus.value = 'idle';
    upcomingError.value = null;
    upcomingAccessDenied.value = false;
    revision.value += 1;
  }

  return {
    pendingCount,
    loadPendingCount,
    day,
    dayDate,
    dayBranchId,
    dayStatus,
    dayError,
    selectedReservationId,
    upcoming,
    upcomingSettings,
    upcomingStatus,
    upcomingError,
    upcomingConsumers,
    upcomingAccessDenied,
    revision,
    loadDay,
    loadUpcoming,
    refreshActive,
    patchReservation,
    patchUpcomingReservation,
    removeFromDay,
    select,
    acquireUpcoming,
    releaseUpcoming,
    clearForBranch,
  };
});
